import React, { useState, useEffect, useRef } from "react";
import { Helmet } from "react-helmet";
import api from "../utils/api";
import ReviewsBar from "../components/ReviewsBar";
import LiveStrip from "../components/LiveStrip";
import "../styles/parkingVsTaxi.css";

export default function TrainVsTaxiGroup() {
  const DEFAULT_PICKUP = "CR4 2DT";
  const DEFAULT_DESTINATION = "HA9 0WS";
  const MARKET_FLAGS = {
    heathrow_official: false,
    edisonlee: false,
    minicab: false,
    online_first: false,
    online_second: false,
    uber_reserve: false
  };

  const AIRPORT_SUGGESTIONS = [
    { code: "LHR T2", label: "Heathrow T2", postcode: "TW6 1EW" },
    { code: "LHR T3", label: "Heathrow T3", postcode: "TW6 1QG" },
    { code: "LHR T4", label: "Heathrow T4", postcode: "TW6 3XA" },
    { code: "LHR T5", label: "Heathrow T5", postcode: "TW6 2GA" },
    { code: "LGW N", label: "Gatwick North", postcode: "RH6 0PJ" },
    { code: "LGW S", label: "Gatwick South", postcode: "RH6 0NP" },
    { code: "STN", label: "Stansted", postcode: "CM24 1RW" },
    { code: "LTN", label: "Luton", postcode: "LU2 9LY" },
    { code: "LCY", label: "London City", postcode: "E16 2PX" },
    { code: "SEN", label: "Southend", postcode: "SS2 6YF" },
    { code: "MAN", label: "Manchester", postcode: "M90 1QX" },
    { code: "BHX", label: "Birmingham", postcode: "B26 3QJ" },
    { code: "EDI", label: "Edinburgh", postcode: "EH12 9DN" },
    { code: "GLA", label: "Glasgow", postcode: "PA3 2SW" },
    { code: "BRS", label: "Bristol", postcode: "BS48 3DY" },
    { code: "NCL", label: "Newcastle", postcode: "NE13 8BZ" },
    { code: "SOU", label: "Southampton", postcode: "SO18 2NL" }
  ];

  const STATIC_POSTCODE_SUGGESTIONS = [
    { code: "RECENT", label: "Group trip", postcode: "CR4 2DT" },
    { code: "RECENT", label: "Group trip", postcode: "HA9 0WS" },
    { code: "RECENT", label: "London", postcode: "SE1 7PB" },
    { code: "RECENT", label: "Manchester", postcode: "M1 1AE" },
    { code: "RECENT", label: "Birmingham", postcode: "B1 1BD" },
    { code: "RECENT", label: "Leeds", postcode: "LS1 4PL" },
    { code: "RECENT", label: "Liverpool", postcode: "L1 1JD" },
    { code: "RECENT", label: "Cardiff", postcode: "CF10 1EP" }
  ];

  const [form, setForm] = useState({
    pickup_postcode: "",
    destination_postcode: "",
    passengers: 1,
    luggage: 0,
    pickup_datetime: ""
  });
  const [pickupSuggestions, setPickupSuggestions] = useState([]);
  const [destinationSuggestions, setDestinationSuggestions] = useState([]);
  const pickupTimer = useRef(null);
  const [pickupWarning, setPickupWarning] = useState("");
  const [pickupFocused, setPickupFocused] = useState(false);

  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const autoSubmitted = useRef(false);
  const loadingTimers = useRef([]);
  const [loadingSteps, setLoadingSteps] = useState([]);
  const [loadingSlow, setLoadingSlow] = useState(false);

  // Prefill defaults: example group journey and next Monday 11:00
  useEffect(() => {
    const nextMondayAt11 = () => {
      const now = new Date();
      const day = now.getDay(); // 0 = Sun
      const daysToMon = (8 - day) % 7 || 7; // always next Monday
      const target = new Date(now);
      target.setDate(now.getDate() + daysToMon);
      target.setHours(11, 0, 0, 0);
      const pad = n => String(n).padStart(2, "0");
      return `${target.getFullYear()}-${pad(target.getMonth() + 1)}-${pad(target.getDate())}T${pad(target.getHours())}:${pad(target.getMinutes())}`;
    };

    setForm(prev => ({
      ...prev,
      pickup_postcode: DEFAULT_PICKUP,
      destination_postcode: DEFAULT_DESTINATION,
      passengers: 4,
      luggage: 2,
      pickup_datetime: nextMondayAt11()
    }));
  }, []);

  // Auto-run comparison once defaults are set
  useEffect(() => {
    if (autoSubmitted.current) return;
    if (form.pickup_postcode && form.destination_postcode && form.pickup_datetime) {
      autoSubmitted.current = true;
      submit();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.pickup_postcode, form.destination_postcode, form.pickup_datetime]);

  const submit = async () => {
    if (pickupWarning) {
      setError("Please enter a valid pickup postcode.");
      return;
    }
    setLoading(true);
    setError(null);
    startLoadingIndicators();

    try {
      const payload = {
        ...form,
        pickup_postcode: form.pickup_postcode.replace(/\(.*?\)/g, "").trim(),
        passengers: Math.min(8, Math.max(3, Number(form.passengers) || 3)),
        luggage: Math.min(8, Math.max(0, Number(form.luggage) || 0))
      };
      const res = await api.post("/api/train/compare", payload);
      setResult(res.data);
    } catch (err) {
      setError("Failed to calculate comparison. Please try again.");
    }

    setLoading(false);
    resetLoadingIndicators();
  };

  const filterSuggestions = (val) => {
    const v = String(val || "").toUpperCase().trim();
    if (v.length < 2) return [];
    const pool = [...AIRPORT_SUGGESTIONS, ...STATIC_POSTCODE_SUGGESTIONS];
    const hits = pool.filter((s) =>
      s.postcode.replace(/\s+/g, "").startsWith(v.replace(/\s+/g, "")) ||
      s.code.toUpperCase().startsWith(v) ||
      s.label.toUpperCase().includes(v)
    );
    const deduped = [];
    const seen = new Set();
    for (const h of hits) {
      if (seen.has(h.postcode)) continue;
      seen.add(h.postcode);
      deduped.push(h);
      if (deduped.length >= 8) break;
    }
    return deduped;
  };

  const isLikelyUkPostcode = (value) => {
    if (!value) return false;
    const norm = String(value).toUpperCase().trim();
    const pcRegex = /^([A-Z]{1,2}[0-9][0-9A-Z]?|[A-Z][0-9][A-Z]|[A-Z]{2}[0-9]{2})\s*[0-9][A-Z]{2}$/;
    return pcRegex.test(norm);
  };

  const filterAirportSuggestions = (val) => {
    const v = String(val || "").toUpperCase().trim();
    if (v.length < 2) return [];
    const hits = AIRPORT_SUGGESTIONS.filter((s) =>
      s.postcode.replace(/\s+/g, "").startsWith(v.replace(/\s+/g, "")) ||
      s.code.toUpperCase().startsWith(v) ||
      s.label.toUpperCase().includes(v)
    );
    const seen = new Set();
    const deduped = [];
    for (const h of hits) {
      if (seen.has(h.postcode)) continue;
      seen.add(h.postcode);
      deduped.push(h);
      if (deduped.length >= 8) break;
    }
    return deduped;
  };

  const renderMoney = v => (v == null ? "—" : `£${Number(v).toFixed(2)}`);

  const fetchPickupSuggestions = (value) => {
    const v = String(value || "");
    if (pickupTimer.current) clearTimeout(pickupTimer.current);
    if (v.length < 2) {
      setPickupSuggestions([]);
      setPickupWarning("");
      return;
    }
    if (v.length >= 4 && !isLikelyUkPostcode(v)) {
      setPickupWarning("Please enter a valid UK postcode (e.g. CR4 2DT).");
    } else {
      setPickupWarning("");
    }
    pickupTimer.current = setTimeout(async () => {
      try {
        const resp = await api.get("/api/postcodes/suggest", {
          params: { q: v, limit: 10 },
        });
        const suggestions = resp.data?.suggestions || [];
        setPickupSuggestions(
          suggestions.map((pc) => ({
            postcode: pc,
            label: "Suggested",
            code: null,
          }))
        );
      } catch (e) {
        setPickupSuggestions([]);
      }
    }, 200);
  };

  const resetLoadingIndicators = () => {
    loadingTimers.current.forEach((t) => clearTimeout(t));
    loadingTimers.current = [];
    setLoadingSteps([]);
    setLoadingSlow(false);
  };

  const markStep = (id) => {
    setLoadingSteps((prev) =>
      prev.map((s) => (s.id === id ? { ...s, status: "done" } : s))
    );
  };

  const startLoadingIndicators = () => {
    resetLoadingIndicators();
    const baseSteps = [
      { id: "distance", label: "Checking distance and travel time", status: "done" },
      { id: "taxi", label: "Checking taxi and ride-hail prices", status: "pending" },
      { id: "tfl", label: "Checking TfL routes (where available)", status: "pending" },
      { id: "nr", label: "Checking National Rail journeys", status: "pending" },
      { id: "compare", label: "Comparing cost, walking and hassle", status: "pending" },
    ];
    setLoadingSteps(baseSteps);
    loadingTimers.current.push(setTimeout(() => markStep("taxi"), 600));
    loadingTimers.current.push(setTimeout(() => markStep("tfl"), 1800));
    loadingTimers.current.push(setTimeout(() => markStep("nr"), 3600));
    loadingTimers.current.push(setTimeout(() => markStep("compare"), 5200));
    loadingTimers.current.push(
      setTimeout(() => setLoadingSlow(true), 10000)
    );
  };

  const renderFareGrid = ({ options = [], legacyCosts = null, providers = null, pax, unavailable = [] }) => {
    const pick = id => options.find(o => o.id === id);
    const unavailableReason = id => unavailable.find(u => u.id === id)?.reason || null;

    const ccg = pick("taxi_ccg") || (legacyCosts?.taxi?.cheapcabgrab != null
      ? { id: "taxi_ccg", label: "Taxi (CheapCabGrab)", total_cost_gbp: legacyCosts.taxi.cheapcabgrab }
      : null);
    const blackCab =
      pick("black_cab_estimate") ||
      (legacyCosts?.taxi?.black_cab
        ? {
            id: "black_cab_estimate",
            label: "Black cab (estimate)",
            total_cost_gbp: legacyCosts.taxi.black_cab.mid,
            breakdown: { range: legacyCosts.taxi.black_cab },
          }
        : null);
    const rideHail =
      pick("ride_hail_estimate") ||
      (legacyCosts?.taxi?.ride_hail
        ? {
            id: "ride_hail_estimate",
            label: "Ride-hail (estimate)",
            total_cost_gbp: legacyCosts.taxi.ride_hail.mid,
            breakdown: { range: legacyCosts.taxi.ride_hail },
          }
        : null);

    const tfl = pick("tfl");
    const nationalRail = pick("national_rail");

    const bandAbsOrPct = (mid, { abs = 1, pct = 0.08, threshold = 120 } = {}) => {
      if (mid == null || Number.isNaN(Number(mid))) return null;
      const m = Number(mid);
      if (m <= threshold) {
        return { min: Number((m - abs).toFixed(2)), mid: Number(m.toFixed(2)), max: Number((m + abs).toFixed(2)) };
      }
      return {
        min: Number((m * (1 - pct)).toFixed(2)),
        mid: Number(m.toFixed(2)),
        max: Number((m * (1 + pct)).toFixed(2))
      };
    };

    const marketExtras = (() => {
      const market = legacyCosts?.taxi?.market;
      if (!market) return [];
      const entries = [
        { key: "heathrow_official", label: "Heathrow official (saloon)", val: market.heathrow_official?.saloon },
        { key: "edisonlee", label: "Edison Lee (saloon)", val: market.edisonlee?.saloon },
        { key: "minicab", label: "Minicab (saloon)", val: market.minicab?.saloon },
        { key: "online_first", label: "Online taxi 1st (saloon)", val: market.online_taxi_first?.mpv },
        { key: "online_second", label: "Online taxi 2nd (saloon)", val: market.online_taxi_second?.mpv },
        { key: "uber_reserve", label: "Uber Reserve (saloon)", val: market.uber_reserve?.saloon }
      ];
      return entries
        .filter(e => MARKET_FLAGS[e.key])
        .map(e => ({ ...e, range: bandAbsOrPct(e.val) }))
        .filter(e => e.range);
    })();

    const railRows = [
      tfl && {
        id: "tfl",
        label: "TfL Rail",
        total: tfl.total_cost_gbp,
        perPerson: tfl.breakdown?.fare_per_person_gbp,
        duration: tfl.breakdown?.duration_minutes,
        walk: tfl.breakdown?.walk_minutes,
        interchanges: tfl.breakdown?.interchanges,
      },
      nationalRail && {
        id: "national_rail",
        label: "National Rail",
        total: nationalRail.total_cost_gbp,
        perPerson: pax ? Number((nationalRail.total_cost_gbp / pax).toFixed(2)) : null,
        duration: nationalRail.breakdown?.end_to_end_mins,
        walk:
          (nationalRail.breakdown?.access_walk_mins || 0) +
          (nationalRail.breakdown?.egress_walk_mins || 0),
        interchanges: nationalRail.legs?.rail?.changes ?? null,
      },
    ].filter(Boolean);

    if (!ccg && railRows.length === 0 && !blackCab && !rideHail) return null;

    return (
      <div className="train-fare-grid">
        <div className="train-fare-group">
          <div className="train-fare-group-heading">🚆 Rail options</div>

          {railRows.map(r => (
            <div key={r.id} className="train-fare-row">
              <div className="train-fare-label">{r.label}</div>
              <div className="train-fare-value">
                {renderMoney(r.total)}
                {r.perPerson != null && (
                  <div className="train-fare-sub">
                    ({renderMoney(r.perPerson)} per person × {pax} passengers)
                  </div>
                )}
                {(r.duration || r.walk || r.interchanges != null) && (
                  <div className="train-fare-sub">
                    {[r.duration != null ? `${r.duration} mins` : null, r.interchanges != null ? `${r.interchanges} changes` : null, r.walk != null ? `${r.walk} mins walk` : null]
                      .filter(Boolean)
                      .join(" • ")}
                  </div>
                )}
              </div>
            </div>
          ))}

          {railRows.length === 0 && (
            <div className="train-fare-row muted-row">
              <div className="train-fare-label">Rail</div>
              <div className="train-fare-value muted">No rail pricing available</div>
            </div>
          )}
        </div>

        <div className="train-fare-group">
          <div className="train-fare-group-heading">🚖 Taxi options</div>

          <div className="train-fare-row ccg-row">
            <div className="train-fare-label">CheapCabGrab (One Way)</div>
            <div className="train-fare-value strong">
              {ccg
                ? renderMoney(ccg.total_cost_gbp)
                : unavailableReason("taxi_ccg") || "Quote unavailable"}
              {!ccg && unavailableReason("taxi_ccg") && (
                <div className="train-fare-sub" style={{ color: "#b45309" }}>
                  {unavailableReason("taxi_ccg")}
                </div>
              )}
            </div>
          </div>

          <div className="train-fare-row muted-row">
            <div className="train-fare-label">Black cab (One Way)</div>
            <div className="train-fare-value muted">
              {blackCab?.breakdown?.range
                ? `${renderMoney(blackCab.breakdown.range.min)} – ${renderMoney(blackCab.breakdown.range.max)}`
                : renderMoney(blackCab?.total_cost_gbp)}
            </div>
          </div>

          <div className="train-fare-row muted-row">
            <div className="train-fare-label">Ride hailing (One Way)</div>
            <div className="train-fare-value muted">
              {rideHail?.breakdown?.range
                ? `${renderMoney(rideHail.breakdown.range.min)} – ${renderMoney(rideHail.breakdown.range.max)}`
                : renderMoney(rideHail?.total_cost_gbp)}
            </div>
          </div>

          {providers?.black_cab_note && (
            <div className="train-fare-row muted-row">
              <div className="train-fare-label">Black cab note</div>
              <div className="train-fare-value muted">{providers.black_cab_note}</div>
            </div>
          )}

          {marketExtras.length > 0 && (
            <>
              <div className="train-fare-row muted-row">
                <div className="train-fare-label">More market prices (beta)</div>
                <div className="train-fare-value muted">
                  {legacyCosts?.taxi?.market?.match ? `Source: ${legacyCosts.taxi.market.match}` : ""}
                </div>
              </div>
              {marketExtras.map((e, idx) => (
                <div key={idx} className="train-fare-row muted-row">
                  <div className="train-fare-label">{e.label}</div>
                  <div className="train-fare-value muted">
                    {renderMoney(e.range.min)} – {renderMoney(e.range.max)}
                  </div>
                </div>
              ))}
            </>
          )}
        </div>
      </div>
    );
  };

  const renderScoresGrid = scores => {
    if (!scores) return null;
    const stressMeter = () => {
      const pax = Number(journey?.passengers ?? form.passengers ?? 1);
      const bags = Number(journey?.luggage ?? form.luggage ?? 0);
      const walk = Number(journey?.total_walk_minutes ?? 0);
      const changes = Number(journey?.interchanges ?? 0);

      const stressBand = (score) => {
        if (score >= 4) return { label: "High", color: "#dc2626", bg: "#fee2e2", icon: "🔴" };
        if (score >= 2) return { label: "Medium", color: "#d97706", bg: "#fef3c7", icon: "🟠" };
        return { label: "Low", color: "#15803d", bg: "#dcfce7", icon: "🟢" };
      };

      const trainScore = (pax >= 3 ? 2 : 0) + (bags >= 2 ? 2 : 0) + (walk > 8 ? 1 : 0) + (changes >= 1 ? 1 : 0);
      const taxiScore = (pax >= 3 ? 1 : 0) + (bags >= 2 ? 1 : 0);

      const trainBand = stressBand(trainScore);
      const taxiBand = stressBand(taxiScore);

      const meterBox = (title, band, notes) => (
        <div style={{ border: "1px solid #e5e7eb", borderRadius: 10, padding: 10, background: "#fff" }}>
          <div style={{ fontWeight: 700 }}>{title}</div>
          <div style={{ marginTop: 4, padding: "6px 8px", borderRadius: 8, background: band.bg, color: band.color, display: "inline-flex", alignItems: "center", gap: 6, fontWeight: 700 }}>
            <span>{band.icon}</span> <span>{band.label} hassle</span>
          </div>
          {notes && <div style={{ marginTop: 6, fontSize: 12, color: "#374151" }}>{notes}</div>}
        </div>
      );

      const trainNotes = [];
      if (pax >= 3) trainNotes.push("Groups pay multiple fares.");
      if (bags >= 2) trainNotes.push("Carrying 2–3 bags across platforms.");
      if (walk > 8) trainNotes.push(`Station walks ~${walk} mins.`);
      if (changes >= 1) trainNotes.push(`${changes} change${changes === 1 ? "" : "s"} en route.`);

      const taxiNotes = [];
      if (pax >= 3) taxiNotes.push("Flat fare splits across the group.");
      if (bags >= 2) taxiNotes.push("No stairs/escalators with luggage.");

      return (
        <div style={{ marginTop: 10, display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(240px,1fr))", gap: 10 }}>
          {meterBox("Train stress meter", trainBand, trainNotes.join(" "))} 
          {meterBox("Taxi stress meter", taxiBand, taxiNotes.join(" "))}
        </div>
      );
    };

    return (
      <>
      <div className="train-score-grid">
        <div className="train-score-item">
          <span className="train-score-label">😴 Fatigue</span>
          <span className="train-score-value">{scores.fatigue ?? "—"}</span>
        </div>
          <div className="train-score-item">
            <span className="train-score-label">👨‍👩‍👧 Family load</span>
            <span className="train-score-value">{scores.family ?? "—"}</span>
          </div>
          <div className="train-score-item">
            <span className="train-score-label">✨ Convenience</span>
            <span className="train-score-value">{scores.convenience ?? "—"}</span>
          </div>
        </div>
        {stressMeter()}
      </>
    );
  };

  const apiData = result?.data || {};
  const options = apiData.options || [];
  const unavailable = apiData.unavailable || [];
  const winnerOption = apiData.winner || null;
  const savingVsNext = apiData.saving_vs_next_best_gbp ?? result?.recommendation?.savings_vs_best ?? null;
  const providers = apiData.meta?.providers || null;
  const meta = apiData.meta || {};
  const legacyCosts = result?.costs || null;
  const journey = result?.journey || {};
  const pax = journey?.passengers ?? form.passengers ?? 1;
  const mode = result?.mode || result?.legacy_mode || (options.length ? "OK" : "NO_RESULT");
  const scores =
    result?.scores ||
    options.find(o => o.id === "tfl")?.scores ||
    options.find(o => o.id === "national_rail")?.scores ||
    options.find(o => o.id === "taxi_ccg")?.scores ||
    null;

  const bestReason = () => {
    if (!winnerOption) return "";
    if (winnerOption.id === "tfl" && winnerOption.breakdown?.price_only_win) {
      const reasons = winnerOption.breakdown?.price_only_reasons || [];
      const msgParts = [];
      if (reasons.includes("includes_bus")) msgParts.push("includes bus segments");
      if (reasons.includes("long_walk")) msgParts.push("long walking");
      if (reasons.includes("many_changes")) msgParts.push("several changes");
      if (reasons.includes("much_slower_than_taxi")) msgParts.push("much slower than taxi");
      const desc = msgParts.length ? msgParts.join(" and ") : "involves extra walking and changes";
      return `Cheapest fare, but ${desc}.`;
    }
    if (winnerOption.mode === "train") {
      return "Cheapest overall on rail, but remember fares multiply per passenger.";
    }
    if (winnerOption.id === "taxi_ccg") {
      return "Fixed taxi price beats multiple train fares for this group size.";
    }
    return `${winnerOption.label} is the cheapest option returned.`;
  };

  const bestLabel = () => {
    if (!winnerOption) return "—";
    if (winnerOption.id === "taxi_ccg") return "Best for groups: Taxi";
    if (winnerOption.id === "national_rail") return "Train (National Rail)";
    if (winnerOption.id === "tfl") {
      return winnerOption.breakdown?.price_only_win ? "Cheapest fare (TfL)" : "Train (TfL)";
    }
    return winnerOption.label || "—";
  };

  const rankedOptions = [...(options || [])]
    .filter(o => typeof o.total_cost_gbp === "number")
    .sort((a, b) => a.total_cost_gbp - b.total_cost_gbp);

  const placeholders = [];
  const tflUnavailable = unavailable.find(u => u.id === "tfl");
  const nrUnavailable = unavailable.find(u => u.id === "national_rail");
  if (tflUnavailable) {
    placeholders.push({
      id: "tfl_unavailable",
      label: "TfL Rail",
      mode: "train",
      unavailableReason: tflUnavailable.reason || "Not available for this journey.",
    });
  }
  if (nrUnavailable) {
    placeholders.push({
      id: "national_rail_unavailable",
      label: "National Rail",
      mode: "train",
      unavailableReason: nrUnavailable.reason || "Not available for this journey.",
    });
  }

  const topThreeOptions = [...rankedOptions, ...placeholders].slice(0, 3);

  const durationFor = (opt) => {
    if (!opt) return null;
    const own =
      opt.breakdown?.duration_minutes ??
      opt.breakdown?.end_to_end_mins ??
      null;
    if (own != null) return own;
    if (opt.mode === "taxi") {
      const km = meta?.distance_km;
      if (km && km > 0) {
        // assume ~50 km/h average incl. traffic; floor at 10 mins
        return Math.max(10, Math.round((km / 50) * 60));
      }
      return null;
    }
    return journey?.duration_minutes ?? null;
  };

  const walkFor = (opt) => {
    if (!opt) return null;
    if (opt.mode === "taxi") return 0;
    const directWalk = opt.breakdown?.walk_minutes;
    const combinedWalk =
      (opt.breakdown?.access_walk_mins || 0) + (opt.breakdown?.egress_walk_mins || 0);
    if (directWalk != null) return directWalk;
    if (combinedWalk) return combinedWalk;
    return journey?.total_walk_minutes ?? null;
  };

  const changesFor = (opt) => {
    if (!opt) return null;
    if (opt.mode === "taxi") return 0;
    return opt.breakdown?.interchanges ?? opt.legs?.rail?.changes ?? journey?.interchanges ?? 0;
  };

  const hassleFor = (opt) => {
    const walk = walkFor(opt) ?? 0;
    const changes = changesFor(opt) ?? 0;
    if (walk >= 20 || changes >= 2) return { label: "High", color: "🔴" };
    if (walk >= 10 || changes >= 1) return { label: "Medium", color: "🟠" };
    return { label: "Low", color: "🟢" };
  };

  const renderHero = () => {
    const ranked = [...(options || [])]
      .filter(o => typeof o.total_cost_gbp === "number")
      .sort((a, b) => a.total_cost_gbp - b.total_cost_gbp);
    const medals = ["GOLD", "SILVER", "BRONZE"];
    const badgeColors = {
      GOLD: { bg: "#fff7e6", text: "#b45309", border: "#facc15", emoji: "🥇" },
      SILVER: { bg: "#f8fafc", text: "#475569", border: "#cbd5e1", emoji: "🥈" },
      BRONZE: { bg: "#fff4ec", text: "#92400e", border: "#f97316", emoji: "🥉" },
    };

    const savingText = Number.isFinite(savingVsNext)
      ? `£${savingVsNext} cheaper than the next best option`
      : "Sorted by lowest total cost.";

    const tiles = medals.map((medal, idx) => {
      const opt = ranked[idx] || null;
      const palette = badgeColors[medal] || badgeColors.GOLD;
      const duration = durationFor(opt);
      const walk = walkFor(opt);
      const changes = changesFor(opt);
      const label =
        opt?.id === "taxi_ccg"
          ? "Taxi"
          : opt?.label
          ? opt.label
          : opt?.mode === "taxi"
          ? "Taxi"
          : "Rail";
      const walkText =
        walk != null && walk >= 10
          ? `🚶 ${walk} min walk`
          : opt?.mode === "taxi"
          ? "🚪 Door-to-door"
          : "🚪 Short walk";
      const isTflPriceOnly = opt?.id === "tfl" && opt.breakdown?.price_only_win;
      const reasons = opt?.breakdown?.price_only_reasons || [];
      const longWalk = walk != null && walk >= 25;
      const manyChanges = changes != null && changes >= 3;
      const muchSlower =
        opt?.breakdown?.duration_minutes &&
        meta?.taxi_duration_mins &&
        opt.breakdown.duration_minutes >= 1.8 * meta.taxi_duration_mins;
      const includesBus = reasons.includes("includes_bus");

      const tflFriction = isTflPriceOnly
        ? [
            includesBus ? "🚌 Bus segments" : null,
            longWalk ? `🚶 ${walk} min walk` : null,
            manyChanges ? "🔁 Several changes" : null,
            muchSlower ? "⏱ Much slower than taxi" : null,
          ]
            .filter(Boolean)
            .join(" • ")
        : null;

      const note =
        tflFriction ||
        (opt?.source && String(opt.source).includes("model")
          ? "⚠ price estimate"
          : null);

      return (
        <div
          key={medal}
          style={{
            border: `1px solid ${palette.border}`,
            borderRadius: 12,
            padding: 12,
            background: palette.bg,
            color: "#111",
          }}
        >
          <div style={{ fontWeight: 800, fontSize: 13, letterSpacing: 0.5, color: palette.text }}>
            {palette.emoji} {medal}
          </div>
          <div style={{ fontSize: 16, fontWeight: 800, marginTop: 6, color: "#111" }}>{label}</div>
          <div style={{ fontSize: 15, fontWeight: 700, marginTop: 4, color: "#111" }}>
            {opt ? renderMoney(opt.total_cost_gbp) : "N/A"}
          </div>
          {opt && (
            <div style={{ display: "flex", flexDirection: "column", gap: 4, marginTop: 6, fontSize: 13 }}>
              <span>{duration ? `⏱ ${duration} min` : "⏱ —"}</span>
              <span>{walkText}</span>
              {note && <span style={{ color: "#b45309" }}>{note}</span>}
            </div>
          )}
        </div>
      );
    });

    return (
      <div
        style={{
          borderRadius: 18,
          padding: 18,
          marginBottom: 16,
          color: "#fff",
          background: "linear-gradient(120deg, #0ea5e9 0%, #22c55e 100%)",
          boxShadow: "0 10px 25px rgba(0,0,0,0.08)",
        }}
      >
        <div style={{ fontWeight: 800, fontSize: 13, letterSpacing: 0.4, textTransform: "uppercase", opacity: 0.9 }}>
          Recommendation
        </div>
        <div style={{ fontSize: 22, fontWeight: 800, marginTop: 6 }}>
          {winnerOption ? `✅ Best: ${bestLabel()}` : "No winner available"}
        </div>
        <div style={{ fontSize: 15, fontWeight: 700, marginTop: 6, opacity: 0.95 }}>
          {bestReason() || "We ranked the options by total cost."}
        </div>
        <div style={{ fontSize: 14, marginTop: 6, opacity: 0.95 }}>{savingText}</div>
      <div style={{ display: "flex", gap: 12, marginTop: 10, fontSize: 12, fontWeight: 700 }}>
        <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>💷 Cheapest</span>
        <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>🧳 Easy with bags</span>
        <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>🚪 Door-to-door</span>
      </div>
        <div style={{ fontSize: 12, opacity: 0.85, marginTop: 8 }}>
          🔒 Uses licensed National Rail & TfL data (via Rail Data Marketplace) plus live taxi pricing.
        </div>
      <div
        style={{
          marginTop: 12,
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
            gap: 10,
          }}
        >
          {tiles}
        </div>
      </div>
    );
  };

  return (
    <div className="parking-wrapper">
      {/* ================= SEO ================= */}


<Helmet>
  <title>Train vs Taxi for Groups (2026): Live Cost & Hassle Comparison</title>

  <meta
    name="description"
    content="For 3+ travellers, train fares multiply fast. Compare live National Rail prices vs fixed taxi costs with walking, changes, luggage and hassle — optimised for groups."
  />

  <meta
    name="keywords"
    content="train vs taxi for groups, cheapest way for 4 people, group train vs taxi calculator, group travel rail vs taxi, stag do travel costs, team trip taxi or train"
  />

  <link
    rel="canonical"
    href="https://www.cheapcabgrab.com/train-vs-taxi-for-groups"
  />

  <meta property="og:title" content="Train vs Taxi for Groups (2026)" />
  <meta
    property="og:description"
    content="Compare live National Rail fares vs fixed taxi prices for groups of 3–8. Includes walking, changes, luggage and hassle scoring."
  />
  <meta
    property="og:url"
    content="https://www.cheapcabgrab.com/train-vs-taxi-for-groups"
  />

  {/* ✅ JSON-LD Structured Data: WebPage, FAQPage, SoftwareApplication, HowTo */}
  <script type="application/ld+json">
    {JSON.stringify({
      "@context": "https://schema.org",
      "@type": "WebPage",
      name: "Train vs Taxi for Groups (2026)",
      url: "https://www.cheapcabgrab.com/train-vs-taxi-for-groups",
      description: "Compare train fares vs taxi costs for groups of 3–8 including walking, luggage and hassle scoring.",
      inLanguage: "en-GB"
    })}
  </script>
  <script type="application/ld+json">
    {JSON.stringify({
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: [
        {
          "@type": "Question",
          name: "Is the train cheaper than a taxi for groups?",
          acceptedAnswer: {
            "@type": "Answer",
            text: "Often for solo travellers. For 3+ people, multiple train tickets quickly add up — this tool shows when a fixed taxi beats the train."
          }
        },
        {
          "@type": "Question",
          name: "What about luggage and walking?",
          acceptedAnswer: {
            "@type": "Answer",
            text: "We factor luggage, walking, and changes. Taxis are door-to-door; trains may involve changes and stations — we include the hassle in the comparison."
          }
        },
        {
          "@type": "Question",
          name: "Do you use live fares?",
          acceptedAnswer: {
            "@type": "Answer",
            text: "Yes. National Rail fares are licensed via the Rail Data Marketplace; taxi prices are fixed for the shown route."
          }
        },
        {
          "@type": "Question",
          name: "Does this work UK-wide?",
          acceptedAnswer: {
            "@type": "Answer",
            text: "Yes. The comparison works UK-wide for any pickup and destination."
          }
        }
      ]
    })}
  </script>
  <script type="application/ld+json">
    {JSON.stringify({
      "@context": "https://schema.org",
      "@type": "SoftwareApplication",
      name: "Train vs Taxi for Groups",
      url: "https://www.cheapcabgrab.com/train-vs-taxi-for-groups",
      description:
        "A UK journey comparison tool showing rail vs taxi prices with walking, luggage and hassle scoring for groups of 3–8 travellers.",
      applicationCategory: "TravelApplication",
      applicationSubCategory: "Journey Planner",
      aggregateRating: {
        "@type": "AggregateRating",
        ratingValue: "4.9",
        reviewCount: "124",
      },
      operatingSystem: "All",
      featureList: [
        "Compares National Rail (where available) vs taxi prices",
        "Group and luggage-aware comparison (3–8 people)",
        "Walking time, changes and hassle scoring",
        "Per-person vs group price clarity"
      ],
      offers: {
        "@type": "Offer",
        price: "0",
        priceCurrency: "GBP"
      }
    })}
  </script>
  <script type="application/ld+json">
    {JSON.stringify({
      "@context": "https://schema.org",
      "@type": "HowTo",
      name: "How to compare train vs taxi costs for your journey",
      description: "Use this calculator to compare taxi, TfL Rail and National Rail costs, time and walking for your journey.",
      totalTime: "PT1M",
      supply: [
        { "@type": "HowToSupply", "name": "Pickup postcode" },
        { "@type": "HowToSupply", "name": "Destination postcode" }
      ],
      tool: [
        { "@type": "HowToTool", "name": "Train vs Taxi calculator" }
      ],
      step: [
        { "@type": "HowToStep", "name": "Enter your pickup postcode", "text": "Enter your pickup postcode (e.g., CR4 2DT) to get live rail fares and localized taxi quotes from your doorstep." },
        { "@type": "HowToStep", "name": "Enter your destination postcode", "text": "Type your destination postcode (e.g., HA9 0WS) so we can find the right stations and routes." },
        { "@type": "HowToStep", "name": "Choose passengers and luggage", "text": "Select how many people are travelling and how many bags you have to see accurate group pricing." },
        { "@type": "HowToStep", "name": "Pick date and time", "text": "Choose the date and time you plan to travel; fares and routing adjust to your chosen slot." },
        { "@type": "HowToStep", "name": "Compare options", "text": "Tap “Compare” to see ranked results with cost, time, walking, and hassle scoring." }
      ]
    })}
  </script>
  <script type="application/ld+json">
    {JSON.stringify({
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "Organization",
          "@id": "https://www.cheapcabgrab.com/#organization",
          name: "CheapCabGrab",
          url: "https://www.cheapcabgrab.com",
          logo: "https://www.cheapcabgrab.com/logo.png",
          sameAs: [
            "https://www.raildeliverygroup.com/our-services/essential-services/rail-data-marketplace.html"
          ],
          memberOf: [
            {
              "@type": "Organization",
              name: "Rail Data Marketplace (RDM)",
              url: "https://www.raildeliverygroup.com/our-services/essential-services/rail-data-marketplace.html",
              alternateName: "National Rail Enquiries Data Partner"
            }
          ]
        },
        {
          "@type": "SoftwareApplication",
          "@id": "https://www.cheapcabgrab.com/train-vs-taxi-for-groups#software",
          name: "Train vs Taxi for Groups",
          author: { "@id": "https://www.cheapcabgrab.com/#organization" },
          isBasedOn: "https://www.nationalrail.co.uk/",
          provider: {
            "@type": "Organization",
            name: "National Rail Enquiries (NRE)",
            url: "https://www.nationalrail.co.uk"
          },
          description: "Live multi-modal journey planner utilizing official National Rail Darwin and RDM data feeds for 100% fare and schedule accuracy.",
          citation: "Official fare data provided under license by Rail Delivery Group (RDG)."
        }
      ]
    })}
  </script>
</Helmet>

      <LiveStrip variant="airports_general" />

      {/* ================= HEADER ================= */}
      <div className="parking-header">
        <h1>Train vs Taxi for Groups (Live Cost & Hassle Comparison)</h1>
        <p>
          For 3+ people, trains often stop being the cheapest once fares multiply.
          Compare live National Rail prices vs fixed taxi costs — including walking, changes, and hassle.
        </p>
        <p style={{ fontSize: 13, color: "#475467", marginTop: 6 }}>
          Myth buster: Group train fares add up fast — taxis are often cheaper door-to-door.
        </p>
        <p style={{ fontSize: 12, color: "#6b7280", marginTop: 4 }}>
          Optimised for groups of 3–8. Want other routes? <a href="/train-vs-taxi">Use the UK-wide Train vs Taxi tool →</a>
        </p>
        <p style={{ fontSize: 11, color: "#6b7280", marginTop: 2 }}>
          Updated: 1 Feb 2026
        </p>
      </div>

      {/* ================= FORM ================= */}
      <div className="parking-form">
        <div className="form-group">
          <label>Your postcode</label>
          <div style={{ position: "relative" }}>
            <input
              className="parking-input"
              value={form.pickup_postcode}
              onChange={(e) => {
                const v = e.target.value;
                setForm({ ...form, pickup_postcode: v });
                fetchPickupSuggestions(v);
              }}
              onFocus={() => setPickupFocused(true)}
              onBlur={() => setPickupFocused(false)}
            />
            {form.pickup_postcode === DEFAULT_PICKUP && !pickupFocused && (
              <div
                style={{
                  position: "absolute",
                  right: 12,
                  top: "50%",
                  transform: "translateY(-50%)",
                  color: "#94a3b8",
                  fontSize: 12,
                  pointerEvents: "none",
                  background: "linear-gradient(90deg, rgba(255,255,255,0) 0%, #fff 35%)",
                  paddingLeft: 12
                }}
              >
                (Enter YOUR postcode)
              </div>
            )}
            {pickupSuggestions.length > 0 && (
              <div style={{ position: "absolute", zIndex: 10, background: "#fff", border: "1px solid #e5e7eb", borderRadius: 8, marginTop: 4, width: "100%", boxShadow: "0 8px 20px rgba(0,0,0,0.08)" }}>
                {pickupSuggestions.map((s, idx) => (
                  <div
                    key={idx}
                    style={{ padding: "8px 10px", cursor: "pointer" }}
                    onClick={() => {
                      setForm({ ...form, pickup_postcode: s.postcode });
                      setPickupSuggestions([]);
                    }}
                  >
                    <div style={{ fontWeight: 700 }}>{s.postcode}</div>
                    {([s.label, s.code].filter(Boolean).length > 0) && (
                      <div style={{ fontSize: 12, color: "#475467" }}>
                        {[s.label, s.code].filter(Boolean).join(" • ")}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
          <div style={{ fontSize: 12, color: "#6b7280", marginTop: 4 }}>
            Example shown — change this to get your personalised comparison.
          </div>
          {pickupWarning && (
            <div style={{ fontSize: 12, color: "#b91c1c", marginTop: 4 }}>
              {pickupWarning}
            </div>
          )}
        </div>

        <div className="form-group">
          <label>Destination Postcode</label>
          <div style={{ position: "relative" }}>
            <input
              className="parking-input"
              value={form.destination_postcode}
              onChange={(e) => {
                const v = e.target.value;
                setForm({ ...form, destination_postcode: v });
                setDestinationSuggestions(filterAirportSuggestions(v));
              }}
            />
            {destinationSuggestions.length > 0 && (
              <div style={{ position: "absolute", zIndex: 10, background: "#fff", border: "1px solid #e5e7eb", borderRadius: 8, marginTop: 4, width: "100%", boxShadow: "0 8px 20px rgba(0,0,0,0.08)" }}>
                {destinationSuggestions.map((s, idx) => (
                  <div
                    key={idx}
                    style={{ padding: "8px 10px", cursor: "pointer" }}
                    onClick={() => {
                      setForm({ ...form, destination_postcode: s.postcode });
                      setDestinationSuggestions([]);
                    }}
                  >
                    <div style={{ fontWeight: 700 }}>{s.postcode}</div>
                    {([s.label, s.code].filter(Boolean).length > 0) && (
                      <div style={{ fontSize: 12, color: "#475467" }}>
                        {[s.label, s.code].filter(Boolean).join(" • ")}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
          <div style={{ fontSize: 12, color: "#6b7280", marginTop: 4 }}>
            This tool is optimised for groups of 3+ travelling UK-wide.
          </div>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
          <div className="form-group">
            <label>Passengers</label>
            <input
              type="number"
              className="parking-input"
              min="3"
              max="8"
              step="1"
              value={form.passengers}
              onChange={(e) =>
                setForm({
                  ...form,
                  passengers:
                    e.target.value === ""
                      ? ""
                      : Math.min(8, Math.max(3, Number(e.target.value)))
                })
              }
              onBlur={() => {
                const num = Number(form.passengers);
                if (Number.isNaN(num)) {
                  setForm({ ...form, passengers: 3 });
                } else {
                  setForm({ ...form, passengers: Math.min(8, Math.max(3, num)) });
                }
              }}
            />
            <div style={{ fontSize: 12, color: "#6b7280", marginTop: 4 }}>
              Optimised for 3–8 people; fares multiply on rail for groups.
            </div>
          </div>

          <div className="form-group">
            <label>Luggage</label>
            <input
              type="number"
              className="parking-input"
              min="0"
              max="8"
              step="1"
              value={form.luggage}
              onChange={(e) =>
                setForm({
                  ...form,
                  luggage:
                    e.target.value === ""
                      ? ""
                      : Math.min(3, Math.max(0, Number(e.target.value)))
                })
              }
              onBlur={() => {
                const num = Number(form.luggage);
                if (Number.isNaN(num)) {
                  setForm({ ...form, luggage: 0 });
                } else {
                  setForm({ ...form, luggage: Math.min(8, Math.max(0, num)) });
                }
              }}
            />
            <div style={{ fontSize: 12, color: "#6b7280", marginTop: 4 }}>
              Standard suitcases. Oversized items may require a larger vehicle.
            </div>
          </div>
        </div>

        <div className="form-group">
          <label>Pickup Date & Time</label>
          <input
            type="datetime-local"
            className="parking-input"
            value={form.pickup_datetime}
            onChange={(e) =>
              setForm({ ...form, pickup_datetime: e.target.value })
            }
          />
        </div>

        <button onClick={submit} className="parking-btn" disabled={loading}>
          {loading ? "⏳ Calculating your best travel option…" : "Compare Your Train vs Taxi"}
        </button>
        <div style={{ fontSize: 12, color: "#6b7280", marginTop: 4 }}>
          Change the postcodes or passengers above to get your personalised result.
        </div>

        {loading && loadingSteps.length > 0 && (
          <div
            style={{
              marginTop: 12,
              padding: 12,
              border: "1px solid #e5e7eb",
              borderRadius: 10,
              background: "#f8fafc",
              position: "sticky",
              top: 0,
              zIndex: 5,
            }}
          >
            <div style={{ fontWeight: 700, marginBottom: 6 }}>🔄 Checking your journey…</div>
            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              {loadingSteps.map((s) => {
                const statusIcon = s.status === "done" ? "✔" : "⏳";
                const statusColor = s.status === "done" ? "#16a34a" : "#9ca3af";
                return (
                  <div key={s.id} style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, color: "#111" }}>
                    <span style={{ color: statusColor }}>{statusIcon}</span>
                    <span>{s.label}</span>
                  </div>
                );
              })}
            </div>
            {loadingSlow && (
              <div style={{ marginTop: 8, fontSize: 12, color: "#92400e" }}>
                National Rail can take a few extra seconds for some routes.
              </div>
            )}
          </div>
        )}

        {error && <p style={{ color: "red", marginTop: "10px" }}>{error}</p>}
      </div>

      {/* ================= RESULTS ================= */}





{/* ================= RESULTS ================= */}
{result && (
  <div className="parking-results">

    {/* ✅ NO TRAIN AVAILABLE WARNING */}
    {(mode === "NO_TRAIN_AVAILABLE" || mode === "TRAIN_UNAVAILABLE") && (
      <div className="best-choice" style={{ background: "#fff7ed", borderLeft: "5px solid #fb923c" }}>
        <div style={{ fontWeight: 700, marginBottom: 6 }}>⚠️ Train route unavailable for this journey.</div>
        <div style={{ fontSize: 14, lineHeight: 1.5 }}>
          TfL rail data may not cover this route (common for non-London postcodes).
          Please check National Rail or Google Maps for train options. Taxi estimates below are still valid.
        </div>

        <div className="train-collapsible-body" style={{ marginTop: 12 }}>
          {renderFareGrid({ options, unavailable, legacyCosts, providers, pax })}
        </div>

        <div style={{ fontSize: 13, color: "#475467", marginTop: 12 }}>
          This comparison uses licensed National Rail journey and fare data, live TfL routing, and live CheapCabGrab pricing to provide an accurate door-to-door view.
        </div>

{/* ✅ INTERNAL LINKS */}
        <div style={{ marginTop: 18 }}>
          <strong>Plan Your Journey:</strong>
          <ul style={{ marginTop: 8, lineHeight: 1.6 }}>
            <li><a href="/heathrow-parking-vs-taxi">Parking vs taxi (main)</a></li>
            <li><a href="/7-day-heathrow-parking-vs-taxi">7-day parking vs taxi</a></li>
            <li><a href="/central-london-heathrow-parking-vs-taxi">Central London comparison</a></li>
            <li><a href="/reading-heathrow-parking-vs-taxi">Reading comparison</a></li>
            <li><a href="/oxford-heathrow-parking-vs-taxi">Oxford comparison</a></li>
            <li><a href="/heathrow-dropoff-free">Free drop-off guide</a></li>
            <li><a href="/heathrow-taxi">Heathrow Taxi Transfers</a></li>
          </ul>
        </div>


      </div>
    )}

    {/* ✅ FULL COMPARISON ONLY WHEN TRAIN EXISTS */}
    {mode !== "NO_RESULT" && (
      <>
        {renderHero()}

        {unavailable.length > 0 && (
          <details
            style={{
              background: "#f8fafc",
              border: "1px solid #e5e7eb",
              borderRadius: 10,
              padding: 10,
              marginBottom: 12,
            }}
            open
          >
            <summary style={{ fontWeight: 700, fontSize: "clamp(13px, 3vw, 14px)", color: "#334155" }}>
              ℹ️ Some options aren’t available for this journey
            </summary>
            <div style={{ marginTop: 6, fontSize: "clamp(12px, 3vw, 13px)", color: "#475467", lineHeight: 1.4 }}>
              {unavailable.map((u, idx) => (
                <div key={idx} style={{ marginBottom: 4 }}>
                  <strong>{u.label || u.id}:</strong> {u.reason || "Not available for this route/time."}
                </div>
              ))}
            </div>
          </details>
        )}

        <div style={{ background: "#f9fafb", border: "1px solid #e5e7eb", borderRadius: 10, padding: 10, marginBottom: 12, display: "flex", flexWrap: "wrap", gap: 10, alignItems: "center", fontWeight: 700, color: "#111827", fontSize: "clamp(12px, 3vw, 14px)" }}>
          <span style={{ fontWeight: 800, marginRight: 6 }}>Why this is best</span>
          <span>💷 Cheapest for groups</span>
          <span>🧳 No station stairs</span>
          <span>🚪 Door-to-door</span>
        </div>
        <div style={{ fontSize: 12, color: "#6b7280", marginBottom: 12 }}>
          Based on live fares, group size, luggage and walking distance.
        </div>

        <div style={{ border: "1px solid #e5e7eb", borderRadius: 12, overflow: "hidden", marginBottom: 12 }}>
          <div style={{ background: "#f3f4f6", padding: "10px 12px", fontWeight: 800, fontSize: "clamp(14px, 3vw, 16px)" }}>Compare at a glance</div>
          <div style={{ padding: 12, overflowX: "auto" }}>
            <div style={{ minWidth: 520 }}>
              <div style={{ display: "grid", gridTemplateColumns: "1.6fr repeat(6, 1fr)", gap: 6, fontWeight: 700, marginBottom: 6, fontSize: "clamp(12px, 3vw, 13px)", color: "#374151" }}>
                <span>Option</span>
                <span>Total</span>
                <span>Per person</span>
                <span>Time</span>
                <span>Walk</span>
                <span>Chg</span>
                <span>Hassle</span>
              </div>
            {topThreeOptions.map((opt, idx) => {
              const duration = typeof opt.total_cost_gbp === "number" ? durationFor(opt) : null;
              const walk = typeof opt.total_cost_gbp === "number" ? walkFor(opt) : null;
              const changes = typeof opt.total_cost_gbp === "number" ? changesFor(opt) : null;
              const hassle = typeof opt.total_cost_gbp === "number" ? hassleFor(opt) : null;
              const label =
                opt.id === "taxi_ccg" ? "Taxi" : opt.label || (opt.mode === "taxi" ? "Taxi" : "Rail");
              const medalEmoji = idx === 0 ? "🥇" : idx === 1 ? "🥈" : "🥉";
              const isPlaceholder = opt.total_cost_gbp == null;
              const perPerson = !isPlaceholder && opt.mode !== "taxi" && pax ? renderMoney(opt.total_cost_gbp / pax) : opt.mode === "taxi" && !isPlaceholder ? renderMoney(opt.total_cost_gbp / pax) : "—";
              return (
                <div key={idx} style={{ display: "grid", gridTemplateColumns: "1.6fr repeat(6, 1fr)", gap: 6, padding: "6px 0", alignItems: "center", borderTop: "1px solid #f1f5f9", fontSize: "clamp(12px, 3vw, 13px)" }}>
                  <span style={{ fontWeight: 700, display: "flex", alignItems: "center", gap: 4 }}>{medalEmoji} {label}</span>
                  <span>{isPlaceholder ? "N/A" : renderMoney(opt.total_cost_gbp)}</span>
                  <span>{isPlaceholder ? "—" : perPerson}</span>
                  <span>{isPlaceholder ? "—" : duration ? `${duration} min` : "—"}</span>
                  <span>{isPlaceholder ? "—" : walk != null ? `${walk} min` : "—"}</span>
                  <span>{isPlaceholder ? "—" : changes ?? "—"}</span>
                  <span>{isPlaceholder ? opt.unavailableReason || "N/A" : `${hassle.color} ${hassle.label}`}</span>
                </div>
              );
            })}
          </div>
        </div>
        </div>

        <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 14, flexWrap: "wrap" }}>
          <button
            className="parking-btn"
            style={{ flex: "0 0 auto", padding: "12px 16px", width: "auto" }}
            onClick={() => (window.location.href = "/")}
          >
            Book Taxi – {renderMoney(options.find(o => o.id === "taxi_ccg")?.total_cost_gbp)}
          </button>
          <div style={{ fontSize: 13, color: "#6b7280" }}>Or explore all options ↓</div>
        </div>

        <details className="train-collapsible" open>
          <summary className="train-summary-bar">🚆 Rail options</summary>
          <div className="train-collapsible-body">
            {options.find(o => o.id === "tfl") && (
              <div style={{ marginBottom: 10 }}>
                <div style={{ fontWeight: 700 }}>TfL Rail</div>
                <div>{renderMoney(options.find(o => o.id === "tfl")?.total_cost_gbp)}</div>
                <div style={{ fontSize: 13, color: "#475467" }}>
                  {durationFor(options.find(o => o.id === "tfl")) ? `${durationFor(options.find(o => o.id === "tfl"))} min • ` : ""}
                  {changesFor(options.find(o => o.id === "tfl")) ?? 0} change • {walkFor(options.find(o => o.id === "tfl")) ?? 0} min walk
                </div>
                <div style={{ fontSize: 12, color: "#6b7280" }}>(£{options.find(o => o.id === "tfl")?.breakdown?.fare_per_person_gbp} per person × {pax})</div>
              </div>
            )}
            {options.find(o => o.id === "national_rail") && (
              <div>
                <div style={{ fontWeight: 700 }}>National Rail</div>
                <div>{renderMoney(options.find(o => o.id === "national_rail")?.total_cost_gbp)}</div>
                <div style={{ fontSize: 13, color: "#475467" }}>
                  {durationFor(options.find(o => o.id === "national_rail")) ? `${durationFor(options.find(o => o.id === "national_rail"))} min • ` : ""}
                  {changesFor(options.find(o => o.id === "national_rail")) ?? 0} change • {walkFor(options.find(o => o.id === "national_rail")) ?? 0} min walk
                </div>
              </div>
            )}
            <div style={{ fontSize: 12, color: "#6b7280", marginTop: 8 }}>
              Group fares multiply per person. Walking time not luggage-friendly.
            </div>
          </div>
        </details>

        <details className="train-collapsible">
          <summary className="train-summary-bar">🚖 Taxi options</summary>
          <div className="train-collapsible-body">
            <div style={{ fontWeight: 700 }}>CheapCabGrab (live) – {renderMoney(options.find(o => o.id === "taxi_ccg")?.total_cost_gbp)}</div>
            <div style={{ marginTop: 6 }}>Black cab (meter est.) – {renderMoney(providers?.black_cab?.min)} – {renderMoney(providers?.black_cab?.max)}</div>
            <div style={{ marginTop: 4 }}>Ride-hailing (estimate) – {renderMoney(providers?.ride_hail?.min)} – {renderMoney(providers?.ride_hail?.max)}</div>
            <div style={{ fontSize: 12, color: "#6b7280", marginTop: 8 }}>
              CheapCabGrab price is fixed. Others are estimates.
            </div>
          </div>
        </details>

        <details className="train-collapsible">
          <summary className="train-summary-bar">😌 Comfort &amp; Stress</summary>
          <div className="train-collapsible-body" style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px,1fr))", gap: 12 }}>
            <div style={{ border: "1px solid #e5e7eb", borderRadius: 10, padding: 12 }}>
              <div style={{ fontWeight: 700 }}>Train stress</div>
              <ul style={{ margin: 8, paddingLeft: 18, color: "#374151" }}>
                <li>🚶 Long station walks</li>
                <li>🧳 Luggage on platforms</li>
                <li>🔁 Changes</li>
              </ul>
            </div>
            <div style={{ border: "1px solid #e5e7eb", borderRadius: 10, padding: 12 }}>
              <div style={{ fontWeight: 700 }}>Taxi comfort</div>
              <ul style={{ margin: 8, paddingLeft: 18, color: "#374151" }}>
                <li>🚪 Door-to-door</li>
                <li>🧳 Bags stay with you</li>
                <li>⏱ No waiting</li>
              </ul>
            </div>
          </div>
        </details>

        <details className="train-collapsible">
          <summary className="train-summary-bar">ℹ️ How we calculate this</summary>
          <div className="train-collapsible-body">
            <div style={{ fontWeight: 700, marginBottom: 6 }}>Data sources</div>
            <ul style={{ margin: 0, paddingLeft: 18, lineHeight: 1.5 }}>
              <li>
                Licensed National Rail journey & fare data (Rail Data Marketplace partner)&nbsp;
                <a href="https://www.nationalrail.co.uk/" target="_blank" rel="noreferrer">nationalrail.co.uk</a>
              </li>
              <li>
                Live TfL journey routing&nbsp;
                <a href="https://tfl.gov.uk/" target="_blank" rel="noreferrer">tfl.gov.uk</a>
              </li>
              <li>Live CheapCabGrab taxi fares</li>
              <li>Estimated ride-hail and black cab pricing</li>
            </ul>
            <div style={{ marginTop: 10 }}>
              👉{" "}
              <a href="https://raildata.org.uk/partnerDetails/5472/details" target="_blank" rel="noreferrer">
                CheapCabGrab Rail Data Marketplace partner profile
              </a>
            </div>
          </div>
        </details>

      </>
    )}
  </div>
)}

<div style={{ marginTop: 18, border: "1px solid #e5e7eb", borderRadius: 12, padding: 14, background: "#f8fafc" }}>
  <div style={{ fontWeight: 800, marginBottom: 6 }}>See other relevant calculators</div>
  <ul style={{ paddingLeft: 18, lineHeight: 1.6, color: "#1f2937", margin: 0 }}>
    <li><a href="/train-vs-taxi">Train vs Taxi (main tool)</a> — includes group travel comparison.</li>
    <li><a href="/train-vs-taxi-birmingham">Train vs Taxi to Birmingham Airport</a>, <a href="/train-vs-taxi-manchester">Manchester Airport</a>, <a href="/train-vs-taxi-edinburgh">Edinburgh Airport</a>, and <a href="/train-vs-taxi-stansted">Stansted</a>.</li>
    <li><a href="/train-vs-taxi-bristol">Train vs Taxi to Bristol Airport</a> — rail includes airport bus transfer.</li>
    <li><a href="/train-vs-taxi-for-groups">Train vs Taxi for Groups</a> — this page is tuned for 3–8 travellers.</li>
    <li><a href="/train-vs-taxi-elizabeth-line">Elizabeth Line vs Taxi</a> and other rail vs taxi tools.</li>
    <li><a href="/travelnews">Airport travel updates & drop-off rules</a>.</li>
  </ul>
</div>

<div style={{ marginTop: 12, border: "1px solid #e5e7eb", borderRadius: 12, padding: 12, background: "#fff" }}>
  <strong>Plan your next group journey:</strong>
  <ul style={{ marginTop: 8, lineHeight: 1.6, marginBottom: 0, paddingLeft: 18, color: "#1f2937" }}>
    <li><a href="/train-vs-taxi">Train vs taxi</a></li>
    <li><a href="/travelnews">Travel updates</a></li>
    <li><a href="/flight-checker">Check your flight</a></li>
    <li><a href="/arrival-assistant">Arrival assistant</a></li>
  </ul>
</div>

<div style={{ marginTop: "40px" }}>
  <ReviewsBar />
</div>







    </div>
  );
}
